import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '@/api/client'
import { unmatchedPayments as api } from '@/api/endpoints'
import { Alert, Modal, Select, Spinner } from '@/components/ui'
import { currencySymbol, formatDate, formatAmount } from '@/lib/utils'
import { PaymentType, type UnmatchedPaymentRow } from '@/types/api'

import ClientOrderPicker from './ClientOrderPicker'

const PAYMENT_TYPES = [PaymentType.Cash, PaymentType.Card, PaymentType.Transfer, PaymentType.Invoice, PaymentType.Other]

/**
 * Spárovať jednu nespárovanú platbu z importu s objednávkou.
 *
 * Suma je pevná - presne to, čo poslala banka (`entry.amount`); operátor
 * len nájde klienta a jeho objednávku (rovnaký `ClientOrderPicker` ako pri
 * „Pridať platbu“) a potvrdí spôsob úhrady.
 */
export default function MatchUnmatchedModal({
  entry,
  onClose,
}: {
  entry: UnmatchedPaymentRow
  onClose: () => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const symbol = currencySymbol()
  const [picked, setPicked] = useState<{ ref: number; clientName: string } | null>(null)
  const [type, setType] = useState<number>(PaymentType.Other)
  const [error, setError] = useState<string | null>(null)

  const match = useMutation({
    mutationFn: () => api.match(entry.id, { ref: picked!.ref, payment_type: type }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payments-unmatched'] })
      void qc.invalidateQueries({ queryKey: ['payments'] })
      void qc.invalidateQueries({ queryKey: ['calendar'] })
      onClose()
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  return (
    <Modal open onClose={onClose} title={t('paymentsPage.matchTitle')}>
      <div className="space-y-3">
        {error ? <Alert variant="error">{error}</Alert> : null}

        <div className="rounded-md border border-slate-200 bg-slate-50/70 px-3 py-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="font-medium text-slate-700">
              {entry.counterparty || t('paymentsPage.unknownCounterparty')}
            </span>
            <span className="text-base font-bold text-slate-800">
              {formatAmount(Number.parseFloat(entry.amount))} {symbol}
            </span>
          </div>
          <div className="mt-0.5 text-xs text-slate-500">
            {formatDate(entry.transaction_date)}
            {entry.variable_symbol ? ` · VS ${entry.variable_symbol}` : ''}
            {entry.message ? ` · ${entry.message}` : ''}
          </div>
        </div>

        {!picked ? (
          <ClientOrderPicker onPick={(ref, clientName) => setPicked({ ref, clientName })} />
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase text-slate-500">{t('reservation.paymentRef')}</div>
                <div className="font-mono font-medium text-slate-800">{picked.ref}</div>
                <div className="text-sm text-slate-600">{picked.clientName}</div>
              </div>
              <button
                type="button"
                className="text-sm text-brand-600 hover:underline"
                onClick={() => setPicked(null)}
              >
                {t('global.change')}
              </button>
            </div>

            <label className="block text-xs uppercase text-slate-500">
              {t('payments.method')}
              <Select
                className="mt-0.5 w-full"
                value={type}
                onChange={(e) => setType(Number(e.target.value))}
              >
                {PAYMENT_TYPES.map((p) => (
                  <option key={p} value={p}>
                    {t(`reservation.payment.${p}`)}
                  </option>
                ))}
              </Select>
            </label>

            <button
              type="button"
              className="btn-primary w-full justify-center"
              disabled={match.isPending}
              onClick={() => {
                setError(null)
                match.mutate()
              }}
            >
              {match.isPending ? <Spinner /> : null}
              {t('paymentsPage.matchConfirm')}
            </button>
          </>
        )}
      </div>
    </Modal>
  )
}
